import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { tw } from "@/utils";

const Modal = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof Dialog.Root>) => {
  return (
    <Dialog.Root {...props}>
      <Dialog.Portal>
        <ModalOverlay />
        {children}
      </Dialog.Portal>
    </Dialog.Root>
  );
};
Modal.displayName = "Modal";

const ModalOverlay = React.forwardRef<
  React.ElementRef<typeof Dialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof Dialog.Overlay>
>(({ className, ...props }, ref) => (
  <Dialog.Overlay
    ref={ref}
    className={tw(
      "fixed inset-0 z-50 bg-neutral-800/80 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
ModalOverlay.displayName = "ModalOverlay";

const ModalTrigger = React.forwardRef<
  React.ElementRef<typeof Dialog.Trigger>,
  React.ComponentPropsWithoutRef<typeof Dialog.Trigger>
>(({ className, children, ...props }, ref) => (
  <Dialog.Trigger ref={ref} className={tw(className)} {...props}>
    {children}
  </Dialog.Trigger>
));
ModalTrigger.displayName = "ModalTrigger";

const ModalContent = React.forwardRef<
  React.ElementRef<typeof Dialog.Content>,
  React.ComponentPropsWithoutRef<typeof Dialog.Content>
>(({ className, children, ...props }, ref) => (
  <Dialog.Content
    ref={ref}
    className={tw(
      "fixed z-50 w-full max-w-2xl",
      "border border-neutral-100 bg-white shadow-lg",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "sm:rounded-lg",
      className,
    )}
    {...props}
  >
    {children}
  </Dialog.Content>
));
ModalContent.displayName = "ModalContent";

const ModalTitle = React.forwardRef<
  React.ElementRef<typeof Dialog.Title>,
  React.ComponentPropsWithoutRef<typeof Dialog.Title>
>(({ className, ...props }, ref) => (
  <Dialog.Title
    ref={ref}
    className={tw("text-lg font-bold text-primary-950", className)}
    {...props}
  />
));
ModalTitle.displayName = "ModalTitle";

const ModalDescription = React.forwardRef<
  React.ElementRef<typeof Dialog.Description>,
  React.ComponentPropsWithoutRef<typeof Dialog.Description>
>(({ className, ...props }, ref) => (
  <Dialog.Description
    ref={ref}
    className={tw(
      "border border-neutral-100 p-6 text-sm text-primary-950",
      className,
    )}
    {...props}
  />
));
ModalDescription.displayName = "ModalDescription";

const ModalClose = React.forwardRef<
  React.ElementRef<typeof Dialog.Close>,
  React.ComponentPropsWithoutRef<typeof Dialog.Close>
>(({ className, ...props }, ref) => (
  <Dialog.Close asChild>
    <button
      ref={ref}
      className={tw(
        "absolute right-4 top-6 rounded-sm p-1 text-neutral-600 opacity-70",
        "transition-opacity hover:opacity-100 focus:outline-none focus:ring-2",
        "focus:ring-neutral-500 focus:ring-offset-2 disabled:pointer-events-none",
        className,
      )}
      {...props}
    >
      <X className="h-5 w-5" />
      <span className="sr-only">Close</span>
    </button>
  </Dialog.Close>
));
ModalClose.displayName = "ModalClose";

const ModalHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={tw("relative flex gap-x-6 p-6", className)}
    {...props}
  />
));
ModalHeader.displayName = "ModalHeader";

const ModalFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={tw("flex w-full justify-center p-6", className)}
    {...props}
  />
));
ModalFooter.displayName = "ModalFooter";

export {
  Modal,
  ModalTrigger,
  ModalContent,
  ModalTitle,
  ModalDescription,
  ModalClose,
  ModalHeader,
  ModalFooter,
};
